import { DndContext, DragEndEvent, DragMoveEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useState } from "react";

import {
  FLOATING_EXPANDABLE_BUBBLE_DRAG_ACTIVATION_DISTANCE,
  FLOATING_EXPANDABLE_BUBBLE_MORPH_DURATION_MS,
  FLOATING_EXPANDABLE_BUBBLE_RADIUS_CLOSE_EASING,
  FLOATING_EXPANDABLE_BUBBLE_RADIUS_OPEN_EASING,
  FLOATING_EXPANDABLE_BUBBLE_SNAP_DURATION_MS
} from "../../constants";
import { useFloatingEntry, useFloatingPositionStoreActions } from "../../floating_position_store";
import type { DragPosition } from "../../hooks/use_floating_resize";
import { useMorphTransition } from "../../hooks/use_morph_transition";
import { usePrefersReducedMotion } from "../../hooks/use_prefers_reduced_motion";
import { type DropZone, getActiveDropZone } from "../../parts/get_active_drop_zone";
import { getBubbleAnchorStyle } from "../../parts/get_bubble_anchor_style";
import { getDropAnchor, getDropPosition, isMagnetPosition } from "../../parts/get_drop_position";
import { getOpenBoxAnchorStyle } from "../../parts/get_open_box_anchor_style";
import { getOpenBoxDropPosition } from "../../parts/get_open_box_drop_position";
import type { BubblePosition } from "../../types";
import { DragHandleProps, FloatingDockDraggable } from "./floating_dock_draggable";

type SnapState = { phase: "idle" } | { phase: "pinned"; dropAnchor: BubblePosition } | { phase: "snapping" };

export type FloatingDockProps = {
  storageKey: string;
  bubbleSize: number;
  zIndex: number;
  dockId: string;
  isOpen: boolean;
  isResizing: boolean;
  dragPosition: DragPosition | null;
  panelSize: { width: number; height: number };
  morphStyle: CSSProperties;
  dropZones?: DropZone[];
  onDragOverZone?: (zoneId: string | null) => void;
  onDropInZone?: (zoneId: string) => void;
  onDragStateChange?: (isDragging: boolean) => void;
  onPanelMouseMove?: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onPanelMouseDown?: (event: ReactMouseEvent<HTMLDivElement>) => void;
  panelCursor?: string;
  renderCollapsed: () => ReactNode;
  renderExpanded: (dragHandleProps: DragHandleProps, areHeaderControlsVisible: boolean) => ReactNode;
};

const POINTER_SENSOR_OPTIONS = {
  activationConstraint: { distance: FLOATING_EXPANDABLE_BUBBLE_DRAG_ACTIVATION_DISTANCE }
};

export const FloatingDock = ({
  storageKey,
  bubbleSize,
  zIndex,
  dockId,
  isOpen,
  isResizing,
  dragPosition,
  panelSize,
  morphStyle,
  dropZones,
  onDragOverZone,
  onDropInZone,
  onDragStateChange,
  onPanelMouseMove,
  onPanelMouseDown,
  panelCursor,
  renderCollapsed,
  renderExpanded
}: FloatingDockProps) => {
  const { position } = useFloatingEntry(storageKey);
  const { setPosition } = useFloatingPositionStoreActions();
  const [snap, setSnap] = useState<SnapState>({ phase: "idle" });
  const prefersReducedMotion = usePrefersReducedMotion();
  const snapDuration = prefersReducedMotion ? 0 : FLOATING_EXPANDABLE_BUBBLE_SNAP_DURATION_MS;
  const morphDuration = prefersReducedMotion ? 0 : FLOATING_EXPANDABLE_BUBBLE_MORPH_DURATION_MS;

  const { isExpanded, isClosing, isOpening } = useMorphTransition(isOpen, morphDuration);
  const areHeaderControlsVisible = isOpen && !isOpening;

  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));
  const halfBubble = bubbleSize / 2;

  useEffect(() => {
    if (snap.phase === "pinned") {
      const raf = requestAnimationFrame(() => setSnap({ phase: "snapping" }));
      return () => cancelAnimationFrame(raf);
    }
    if (snap.phase === "snapping") {
      const timer = setTimeout(() => setSnap({ phase: "idle" }), FLOATING_EXPANDABLE_BUBBLE_SNAP_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [snap]);

  useEffect(() => {
    const handleResizeEnd = (event: Event) => {
      const { detail } = event as CustomEvent<{ storageKey: string; dragPosition: DragPosition }>;
      if (detail.storageKey !== storageKey) {
        return;
      }

      const { top, left } = detail.dragPosition;
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const newPosition = getOpenBoxDropPosition({ top, left }, viewport, panelSize);
      setPosition(storageKey, newPosition);
    };

    window.addEventListener("floating-resize-end", handleResizeEnd);
    return () => window.removeEventListener("floating-resize-end", handleResizeEnd);
  }, [storageKey, panelSize, setPosition]);

  const handleDragStart = () => {
    setSnap({ phase: "idle" });
    onDragStateChange?.(true);
  };

  const handleDragMove = ({ active }: DragMoveEvent) => {
    if (isOpen || !dropZones?.length) {
      return;
    }
    onDragOverZone?.(getActiveDropZone(active.rect.current.translated, dropZones));
  };

  const handleDragEnd = ({ active }: DragEndEvent) => {
    const finishDrag = () => {
      onDragOverZone?.(null);
      onDragStateChange?.(false);
    };

    const rect = active.rect.current.translated;
    if (!rect) {
      finishDrag();
      return;
    }

    if (!isOpen && dropZones?.length) {
      const zoneId = getActiveDropZone(rect, dropZones);
      if (zoneId) {
        onDropInZone?.(zoneId);
        finishDrag();
        return;
      }
    }

    const viewport = { width: window.innerWidth, height: window.innerHeight };
    if (isOpen) {
      setPosition(
        storageKey,
        getOpenBoxDropPosition({ left: rect.left, top: rect.top }, viewport, panelSize)
      );
      setSnap({ phase: "idle" });
    } else {
      const point = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      const dropped = getDropPosition(point, viewport);
      setPosition(storageKey, dropped);
      setSnap(
        isMagnetPosition(dropped)
          ? { phase: "pinned", dropAnchor: getDropAnchor(dropped, point, viewport, halfBubble) }
          : { phase: "idle" }
      );
    }

    finishDrag();
  };

  const handleDragCancel = () => {
    setSnap({ phase: "idle" });
    onDragOverZone?.(null);
    onDragStateChange?.(false);
  };

  const renderedPosition = snap.phase === "pinned" ? snap.dropAnchor : position;

  const computedAnchorStyle: CSSProperties = {
    position: "fixed",
    zIndex,
    ...(isClosing ? { overflow: "hidden" } : {}),
    ...(isExpanded
      ? getOpenBoxAnchorStyle(
          position,
          halfBubble,
          { width: window.innerWidth, height: window.innerHeight },
          panelSize
        )
      : getBubbleAnchorStyle(renderedPosition, halfBubble))
  };

  const anchorStyle: CSSProperties =
    isResizing && dragPosition !== null
      ? { position: "fixed", zIndex, top: dragPosition.top, left: dragPosition.left }
      : computedAnchorStyle;

  const transitionStyle: CSSProperties =
    snap.phase === "snapping"
      ? {
          transitionProperty: "top, left, right, bottom",
          transitionDuration: `${snapDuration}ms`
        }
      : isExpanded
        ? {
            transitionProperty: "width, height, border-radius",
            transitionDuration: `${morphDuration}ms`,
            transitionTimingFunction: `ease, ease, ${
              isClosing
                ? FLOATING_EXPANDABLE_BUBBLE_RADIUS_CLOSE_EASING
                : FLOATING_EXPANDABLE_BUBBLE_RADIUS_OPEN_EASING
            }`
          }
        : { transition: "none" };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToWindowEdges]}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <FloatingDockDraggable
        id={dockId}
        isExpanded={isExpanded}
        areHeaderControlsVisible={areHeaderControlsVisible}
        anchorStyle={anchorStyle}
        morphStyle={morphStyle}
        transitionStyle={isResizing ? { transition: "none" } : transitionStyle}
        panelCursor={panelCursor}
        onMouseMove={onPanelMouseMove}
        onMouseDown={onPanelMouseDown}
        renderCollapsed={renderCollapsed}
        renderExpanded={renderExpanded}
      />
    </DndContext>
  );
};
